import {Component, HostListener, Inject, OnInit} from '@angular/core';
import {CookieService} from './core/services/cookie.service';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, NavigationCancel, NavigationEnd, NavigationError, NavigationStart, Router} from '@angular/router';
import {Title} from '@angular/platform-browser';
import {filter, takeWhile} from 'rxjs/operators';
import {NGXLogger} from 'ngx-logger';
import {Language} from './core/constants/language';
import {AuthService} from './core/auth/auth.service';
import {DOCUMENT, Location} from '@angular/common';
import {DsgvoService} from '@app/core/services/dsgvo.service';
import {DeviceService} from './core/services/device.service';
import {ApiNotificationService} from './core/services/api-notification.service';
import {interval, Subscription, timer} from 'rxjs';
import {RoamingService} from './core/services/roaming.service';
import {ApiStatusInfoService} from './shared/components/card-device/services/api-status-info.service';
import {GroupService} from './core/services/group.service';
import {SyncService} from '@app/core/services/sync.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit {
  private  routeLoadingIntervalSubscription: Subscription;

  /** Toggle loading indicator when navigating between routes */
  public routeLoading = false;
  public routeLoadingProgress = 0;

  // PWA
  private deferredPrompt: any;


  constructor(
    private translate: TranslateService,
    private cookie: CookieService,
    private router: Router,
    private titleService: Title,
    private activatedRoute: ActivatedRoute,
    private logger: NGXLogger,
    private location: Location,
    private auth: AuthService,
    private device: DeviceService,
    private apiNotification: ApiNotificationService,
    private roaming: RoamingService,
    private dsgvo: DsgvoService,
    private apiStatusInfo: ApiStatusInfoService,
    private groupService: GroupService,
    private sync: SyncService,
    @Inject(DOCUMENT) private document: Document
  ) {

    // Init
    this.auth.init();
    this.device.init();
    this.apiNotification.init();
    this.roaming.init();
    this.dsgvo.init();
    this.apiStatusInfo.init();
    this.groupService.init();
    this.supressPWAInstallPrompt();


    /* Language stuff */

    /*
     * Don't set default lang at module import because this is too early and will result in a cyclic dependency error
     * in which AuthInterceptor and AuthService are involved
     */
    this.translate.defaultLang = 'de_DE';

    /*
     * Use this complicated way of getting the "lang" query parameter because we can't use `activatedRoute.queryParams`.
     * The latter will emit the first time before the actual query param was parsed by Angular
     * (--> The parameter is always null in the first emit)
     * and therefore we would change the language intially without knowing the query parameter.
     * And changing the language two times after another too fast applies the wrong language by chance.
     */
    const rawQueryParams = this.location.path().split('?')[1]?.split('&');
    const lang = rawQueryParams?.filter(rawParam => rawParam.startsWith('lang='))[0]?.split('=')[1] ?? '';

    if (lang === 'en' || (lang === '' && this.cookie.getCookie('language') === Language.English)) {
      this.auth.switchLanguage(Language.English);
    } else if (lang === 'fr' || (lang === '' && this.cookie.getCookie('language') === Language.French)) {
      this.auth.switchLanguage(Language.French);
    } else {
      this.auth.switchLanguage(Language.German);
    }

  }



  public ngOnInit() {
    this.updatePageTitle();
    this.initNavigationLoadingIndicator();
    this.sessionCheckTimer();
  }

  /**
   * Updates the page title if the route was changed.
   *
   * Uses one of the following properties that should be added to the custom route data:
   * - title: Plain title
   * - titleKey: language string of the page title
   */
  private updatePageTitle() {
    // Save title that was initially set in index.html
    const originalTitle = this.titleService.getTitle();

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe(() => {
      let child = this.activatedRoute.firstChild;
      while (child.firstChild) {
        child = child.firstChild;
      }

      if (child.snapshot.data.titleKey) {
        this.translate.get(child.snapshot.data.titleKey).subscribe((value) => {
          this.titleService.setTitle(originalTitle + ' - ' + value);
        });
      } else if (child.snapshot.data.title) {
        this.titleService.setTitle(originalTitle + ' - ' + child.snapshot.data.title);
      } else {
        // Fallback to original title if no title was provided
        this.logger.debug('Got no page title from route data, fallback to original title');
        this.titleService.setTitle(originalTitle);
      }
    });
  }

  private initNavigationLoadingIndicator() {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.routeLoading = true;
        this.routeLoadingProgress = 0;

        // If loading is too slow, the progressbar will stop at 80%
        this.routeLoadingIntervalSubscription = interval(500)
          .pipe(takeWhile(() => this.routeLoadingProgress <= 80))
          .subscribe(() => {
            this.routeLoadingProgress = Math.min(this.routeLoadingProgress + 10, 100);
          });
      } else if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.routeLoadingIntervalSubscription?.unsubscribe();

        // Delay final assignment of value because if the route is already cached, we arrive here directly after NavigationStart
        // and no progressbar animation would be shown
        timer(10).subscribe(() => {
          this.routeLoadingProgress = 100;
        });

        // Wait for completion of the progressbar animation to 100%
        timer(500).subscribe(() => {
          this.routeLoading = false;
          this.routeLoadingProgress = 0;
        });
      }
    });
  }

  /**
   * Update the --app-height CSS variable to make the current window height available
   * (this height is useful because it does NOT include the height of the title bar of mobile browsers like Chrome)
   */
  @HostListener('window:resize', ['$event'])
  private onResize(event: UIEvent) {
    this.document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
  }

  sessionCheckTimer() {
     timer(0, 30000).subscribe(() => {
       if (this.router.url === '/feedback') {
         this.logger.info('sessionCheckTimer SKIP for feedback form');
         return;
       }
       if (this.auth.isLoggedIn) {
         this.sync.sessionCheck().subscribe((response) => {
           // do nothing here, will automatically log out if session expired
           this.logger.info('sessionCheck response=' + response);
         });
       }
    });
  }

  private supressPWAInstallPrompt() {
    window.addEventListener('beforeinstallprompt', (e) => {

      // Prevent the installation notice from being displayed automatically
      e.preventDefault();

      this.deferredPrompt = e;
    });
  }


}
